import React, { useState, useCallback } from "react";
import "@/App.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LoginPage } from "./pages/LoginPage";
import { useAppData } from "./api/useAppData";
import { useAppearance } from "./hooks/useAppearance";
import { Header } from "./components/Header";
import { StatsBar } from "./components/StatsBar";
import { TabBar } from "./components/TabBar";
import { TicketGrid } from "./components/TicketGrid";
import { TicketModal } from "./components/TicketModal";
import { SellersTab } from "./components/SellersTab";
import { LogTab } from "./components/LogTab";
import { ReportTab } from "./components/ReportTab";
import { AssignTab } from "./components/AssignTab";
import { DepositTab } from "./components/DepositTab";
import { RenewalsTab } from "./components/RenewalsTab";
import { DrawingsTab } from "./components/DrawingsTab";
import { CheckInTab } from "./components/CheckInTab";
import { ToastHost, showAppToast } from "./components/Toast";
import { UpdateBanner } from "./components/UpdateBanner";
import { InstallBanner } from "./components/InstallBanner";
import { ProfileModal } from "./components/ProfileModal";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/PasswordResetPages";

const MainApp = ({ appearance }) => {
  const { user, logout, updateProfile, changePassword } = useAuth();
  const data = useAppData(user);
  const [tab, setTab] = useState(user.role === "checkin_volunteer" ? "checkin" : "grid");
  const [openTicket, setOpenTicket] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const closeModal = useCallback(() => setOpenTicket(null), []);

  const handleRelease = useCallback(async (n) => {
    const res = await data.releaseTicket(n);
    if (res.ok) { showAppToast(`Ticket #${n} released`); closeModal(); }
    return res;
  }, [data, closeModal]);

  const handleMarkSold = useCallback(async (n) => {
    const res = await data.markSold(n);
    if (res.ok) { showAppToast(`Ticket #${n} marked as sold`); closeModal(); }
    return res;
  }, [data, closeModal]);

  const handleMarkFundsReceived = useCallback(async (n, tender) => {
    const res = await data.markFundsReceived(n, tender);
    return res;
  }, [data]);

  const isAdmin = user.role === "admin";
  const isSeller = user.role === "seller";
  const isCheckinVolunteer = user.role === "checkin_volunteer";
  const isReadOnlyYear = data.isReadOnlyYear;

  // When viewing a past (closed) year, restrict navigation to read-only views.
  const tabs = isReadOnlyYear
    ? [
        { key: "grid", label: "Ticket Grid", short: "Grid", testId: "tab-grid" },
        { key: "log", label: "Activity Log", short: "Log", testId: "tab-log" },
        !isSeller && !isCheckinVolunteer && { key: "report", label: "Report", short: "Report", testId: "tab-report" },
        isAdmin && { key: "drawings", label: "Drawings", short: "Draws", testId: "tab-drawings" },
      ].filter(Boolean)
    : isCheckinVolunteer
    ? [
        // Check-in volunteers see ONLY the drawing-night check-in tab.
        { key: "checkin", label: "Check-In", short: "Check-In", testId: "tab-checkin" },
      ]
    : [
        { key: "grid", label: "Ticket Grid", short: "Grid", testId: "tab-grid" },
        { key: "renewals", label: "Renewals", short: "Renewals", testId: "tab-renewals" },
        { key: "sellers", label: "Sellers", short: "Sellers", testId: "tab-sellers" },
        { key: "log", label: "Activity Log", short: "Log", testId: "tab-log" },
        !isSeller && { key: "report", label: "Report", short: "Report", testId: "tab-report" },
        isAdmin && { key: "drawings", label: "Drawings", short: "Draws", testId: "tab-drawings" },
        { key: "checkin", label: "Check-In", short: "Check-In", testId: "tab-checkin" },
        isAdmin && { key: "deposit", label: "Deposit", short: "Deposit", testId: "tab-deposit" },
        isAdmin && { key: "assign", label: "Assign Tickets", short: "Assign", testId: "tab-assign" },
      ].filter(Boolean);

  // If the user was on a tab that no longer exists after switching years, fall back to the first available tab.
  React.useEffect(() => {
    if (!tabs.find((t) => t.key === tab)) setTab(tabs[0]?.key || "grid");
  }, [tabs, tab]);

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-page)", color: "var(--ink-primary)" }} data-testid="app-root">
      <Header
        year={data.year}
        onYearChange={data.setYear}
        user={user}
        onLogout={logout}
        onOpenProfile={() => setProfileOpen(true)}
        currentYear={data.settings?.current_year}
        historyYears={data.settings?.history_years || []}
        isReadOnly={data.isReadOnlyYear}
        showToast={showAppToast}
      />
      {!isCheckinVolunteer && <StatsBar stats={data.stats} />}
      <TabBar active={tab} onChange={setTab} tabs={tabs} />

      <main
        id={`tab-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`tab-button-${tab}`}
        data-testid={`tab-panel-${tab}`}
      >
        {tab === "grid" && (
          <TicketGrid
            tickets={data.tickets}
            currentUser={user}
            historyOverview={data.historyOverview}
            yearStartedAt={data.settings?.year_started_at}
            onOpenTicket={(n) => setOpenTicket(n)}
          />
        )}
        {tab === "sellers" && (
          <SellersTab
            users={data.users}
            tickets={data.tickets}
            currentUser={user}
            onAdd={data.addUser}
            onRemove={data.removeUser}
            onUpdate={data.updateUser}
            onResetPassword={data.resetUserPassword}
            showToast={showAppToast}
          />
        )}
        {tab === "log" && <LogTab log={data.log} year={data.year} currentUser={user} />}
        {tab === "renewals" && (
          <RenewalsTab
            tickets={data.tickets}
            currentUser={user}
            getTicketHistory={data.getTicketHistory}
            onMarkCalled={data.markCalled}
            onOpenTicket={(n) => setOpenTicket(n)}
          />
        )}
        {tab === "report" && (
          <ReportTab
            tickets={data.tickets}
            stats={data.stats}
            sellers={data.users.filter((u) => u.role === "seller")}
            year={data.year}
            settings={data.settings}
            currentUser={user}
            onCloseYear={data.closeYear}
            onStartYear={data.startYear}
            onImportHistory={data.importHistory}
            onSyncBrevoContacts={data.syncBrevoContacts}
            showToast={showAppToast}
          />
        )}
        {tab === "assign" && isAdmin && (
          <AssignTab tickets={data.tickets} users={data.users} onAssign={data.assignTickets} onUnassign={data.unassignTickets} onOpenTicket={(n) => setOpenTicket(n)} showToast={showAppToast} />
        )}
        {tab === "deposit" && isAdmin && (
          <DepositTab
            tickets={data.tickets}
            currentUser={user}
            onBulkMarkFunds={data.bulkMarkFundsReceived}
            drawings={data.drawings}
            fetchReminderEligible={data.getPaymentReminderEligible}
            sendPaymentReminders={data.sendPaymentReminders}
            showToast={showAppToast}
          />
        )}
        {tab === "drawings" && isAdmin && (
          <DrawingsTab
            drawings={data.drawings}
            tickets={data.tickets}
            year={data.year}
            isReadOnlyYear={data.isReadOnlyYear}
            onCreate={data.createDrawing}
            onUpdate={data.updateDrawing}
            onDelete={data.deleteDrawing}
            onDraw={data.performDraw}
            onClear={data.clearDrawing}
            onSetPrizePaid={data.setPrizePaid}
            fetchEligible={data.fetchEligible}
            showToast={showAppToast}
          />
        )}
        {tab === "checkin" && (
          <CheckInTab currentUser={user} showToast={showAppToast} />
        )}
      </main>

      {openTicket != null && data.tickets[openTicket] && (
        <TicketModal
          key={openTicket}
          number={openTicket}
          ticket={data.tickets[openTicket]}
          currentUser={user}
          users={data.users}
          onClose={closeModal}
          onRelease={handleRelease}
          onMarkSold={handleMarkSold}
          onMarkFundsReceived={handleMarkFundsReceived}
          onRecord={data.recordTicket}
          onUpdateBuyer={data.updateBuyer}
          onResendEmail={data.resendEmail}
          onAssignSeller={(n, sid) => data.assignTickets([n], sid)}
          onUnassignSeller={(n) => data.unassignTickets([n])}
          getHistory={data.getTicketHistory}
          showToast={showAppToast}
        />
      )}

      <ToastHost />
      <UpdateBanner />
      <InstallBanner />

      {profileOpen && (
        <ProfileModal
          user={user}
          onClose={() => setProfileOpen(false)}
          updateProfile={updateProfile}
          changePassword={changePassword}
          appearance={appearance}
          showToast={showAppToast}
        />
      )}

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 pb-32 sm:pb-6 pt-6 text-xs" style={{ color: "var(--ink-tertiary)" }}>
        Red Hook Rhinebeck Elks Lodge #2022 · Four Hundred Club · {data.year}
      </footer>
    </div>
  );
};

const Gate = () => {
  const { user } = useAuth();
  // Single source of truth for theme + contrast — passed down to MainApp and
  // to the Login/Password-reset screens implicitly (via data-theme on <html>).
  const appearance = useAppearance();
  const [authView, setAuthView] = useState(() => {
    // Detect ?reset-password path in URL on initial load
    if (typeof window !== "undefined" && window.location.pathname === "/reset-password") {
      return "reset";
    }
    return "login";
  });

  const resetToken = React.useMemo(() => {
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams(window.location.search);
    return params.get("token") || "";
  }, [authView]);

  const goToLogin = useCallback(() => {
    if (typeof window !== "undefined" && window.location.pathname !== "/") {
      window.history.replaceState({}, "", "/");
    }
    setAuthView("login");
  }, []);

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--surface-page)", color: "var(--ink-primary)" }} data-testid="auth-loading">
        <p className="text-sm" style={{ color: "#605E5C" }}>Loading…</p>
      </div>
    );
  }
  if (!user) {
    if (authView === "forgot") {
      return <ForgotPasswordPage onBackToLogin={goToLogin} />;
    }
    if (authView === "reset") {
      if (!resetToken) {
        return <ForgotPasswordPage onBackToLogin={goToLogin} />;
      }
      return <ResetPasswordPage token={resetToken} onSuccess={goToLogin} onBackToLogin={goToLogin} />;
    }
    return <LoginPage onForgotPassword={() => setAuthView("forgot")} />;
  }
  return <MainApp appearance={appearance} />;
};

function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}

export default App;
